import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { incluyeTexto } from '../utils/texto';
import { motion, MotionConfig } from 'framer-motion';
import { Calendar, ChevronLeft, ChevronRight, Users, MapPin, X, Trash2, GripVertical, Clock, BarChart3, List, Search, User, ExternalLink } from 'lucide-react';
import axiosInstance from '../utils/axiosConfig';
import { useAuth } from '../context/AuthContext';

// La linea de ahora y la sombra del pasado avanzan con el reloj (un tic por
// segundo): se deslizan entre tic y tic para correr continuas.
const AHORA_DESLIZA = 'left 1s linear';
const AHORA_DESLIZA_ANCHO = 'width 1s linear';
// Las vistas previas al crear, mover o estirar una reserva se deslizan de media
// hora en media hora en vez de saltar.
const PREVIA_DESLIZA = 'left 140ms cubic-bezier(0.22, 1, 0.36, 1), width 140ms cubic-bezier(0.22, 1, 0.36, 1)';

// Componente Mini Calendario
const MiniCalendar = ({ selectedDate, onSelectDate, reservasDates }) => {
  const [viewDate, setViewDate] = useState(new Date(selectedDate));

  useEffect(() => {
    setViewDate(new Date(selectedDate));
  }, [selectedDate]);

  const daysInMonth = new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 0).getDate();
  const firstDayOfMonth = new Date(viewDate.getFullYear(), viewDate.getMonth(), 1).getDay();

  const monthNames = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
  const dayNames = ['Do', 'Lu', 'Ma', 'Mi', 'Ju', 'Vi', 'Sa'];

  const prevMonth = () => {
    setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setViewDate(new Date(viewDate.getFullYear(), viewDate.getMonth() + 1, 1));
  };

  const isToday = (day) => {
    const today = new Date();
    return day === today.getDate() &&
      viewDate.getMonth() === today.getMonth() &&
      viewDate.getFullYear() === today.getFullYear();
  };

  const isSelected = (day) => {
    return day === selectedDate.getDate() &&
      viewDate.getMonth() === selectedDate.getMonth() &&
      viewDate.getFullYear() === selectedDate.getFullYear();
  };

  const hasReservas = (day) => {
    const dateStr = `${viewDate.getFullYear()}-${(viewDate.getMonth() + 1).toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
    return reservasDates.includes(dateStr);
  };

  const handleDayClick = (day) => {
    const newDate = new Date(viewDate.getFullYear(), viewDate.getMonth(), day);
    onSelectDate(newDate);
  };

  const days = [];
  // Espacios vacíos antes del primer día
  for (let i = 0; i < firstDayOfMonth; i++) {
    days.push(<div key={`empty-${i}`} className="h-8"></div>);
  }
  // Días del mes
  for (let day = 1; day <= daysInMonth; day++) {
    const hasRes = hasReservas(day);
    days.push(
      <button
        key={day}
        onClick={() => handleDayClick(day)}
        className={`h-8 w-8 rounded-full text-sm font-medium transition-all relative
          ${isSelected(day) ? 'bg-blue-600 text-white' : ''}
          ${isToday(day) && !isSelected(day) ? 'bg-blue-100 text-blue-700' : ''}
          ${!isSelected(day) && !isToday(day) ? 'hover:bg-gray-100 text-gray-700' : ''}
        `}
      >
        {day}
        {hasRes && !isSelected(day) && (
          <div className="absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-blue-500"></div>
        )}
      </button>
    );
  }

  return (
    <div>
      {/* Header del calendario */}
      <div className="flex items-center justify-between mb-4">
        <button onClick={prevMonth} className="p-1 hover:bg-gray-100 rounded">
          <ChevronLeft size={16} />
        </button>
        <span className="font-medium text-gray-900">
          {monthNames[viewDate.getMonth()]} {viewDate.getFullYear()}
        </span>
        <button onClick={nextMonth} className="p-1 hover:bg-gray-100 rounded">
          <ChevronRight size={16} />
        </button>
      </div>

      {/* Días de la semana */}
      <div className="grid grid-cols-7 gap-1 mb-2">
        {dayNames.map(day => (
          <div key={day} className="h-8 flex items-center justify-center text-xs font-medium text-gray-500">
            {day}
          </div>
        ))}
      </div>

      {/* Días del mes */}
      <div className="grid grid-cols-7 gap-1">
        {days}
      </div>
    </div>
  );
};

// Componente Buscador de Empleados
const EmployeeSearch = ({ value, onChange, empleados }) => {
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [filtered, setFiltered] = useState([]);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (search.length >= 2) {
      const results = empleados.filter(emp =>
        incluyeTexto(emp.nombre, search) ||
        emp.ficha.toLowerCase().includes(search.toLowerCase())
      ).slice(0, 8);
      setFiltered(results);
      setIsOpen(results.length > 0);
    } else {
      setFiltered([]);
      setIsOpen(false);
    }
  }, [search, empleados]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
        inputRef.current && !inputRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (emp) => {
    onChange(emp);
    setSearch('');
    setIsOpen(false);
  };

  const handleClear = () => {
    onChange(null);
    setSearch('');
  };

  return (
    <div className="relative">
      {value ? (
        <div className="flex items-center gap-2 p-2 bg-blue-50 border border-blue-200 rounded-lg">
          <User size={16} className="text-blue-600" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">{value.nombre}</p>
            <p className="text-xs text-gray-500">{value.ficha}</p>
          </div>
          <button onClick={handleClear} className="text-gray-400 hover:text-gray-600">
            <X size={16} />
          </button>
        </div>
      ) : (
        <>
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nombre o ficha..."
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              onFocus={() => search.length >= 2 && setIsOpen(true)}
            />
          </div>
          {isOpen && (
            <div
              ref={dropdownRef}
              className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto"
            >
              {filtered.map(emp => (
                <button
                  key={emp.id}
                  onClick={() => handleSelect(emp)}
                  className="w-full px-3 py-2 text-left hover:bg-blue-50 flex items-center gap-2 transition"
                >
                  <User size={14} className="text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">{emp.nombre}</p>
                    <p className="text-xs text-gray-500">{emp.ficha}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

// Componente Panel de Estadísticas
const StatsPanel = ({ reservas, salas, totalSlots, slotMinutes, timeToSlot }) => {
  const stats = useMemo(() => {
    // Calcular horas ocupadas
    let totalOcupado = 0;
    reservas.forEach(r => {
      const start = timeToSlot(r.hora_inicio);
      const end = timeToSlot(r.hora_fin);
      totalOcupado += (end - start) * slotMinutes;
    });
    const horasOcupadas = totalOcupado / 60;
    const horasDisponibles = (totalSlots * slotMinutes / 60) * salas.length;
    const porcentajeOcupacion = horasDisponibles > 0 ? (horasOcupadas / horasDisponibles) * 100 : 0;

    // Sala más usada
    const salaCount = {};
    reservas.forEach(r => {
      salaCount[r.sala_id] = (salaCount[r.sala_id] || 0) + 1;
    });
    const salaMasUsadaId = Object.entries(salaCount).sort((a, b) => b[1] - a[1])[0]?.[0];
    const salaMasUsada = salas.find(s => s.id === parseInt(salaMasUsadaId));

    // Próximo slot disponible
    const now = new Date();
    const currentHour = now.getHours();
    const currentMinutes = now.getMinutes();
    let proximoDisponible = null;

    // Buscar próximo slot disponible
    for (let slotIdx = 0; slotIdx < totalSlots; slotIdx++) {
      const slotHour = 8 + Math.floor(slotIdx * slotMinutes / 60);
      const slotMin = (slotIdx * slotMinutes) % 60;

      if (slotHour > currentHour || (slotHour === currentHour && slotMin > currentMinutes)) {
        // Verificar si hay alguna sala disponible en este slot
        for (const sala of salas) {
          const ocupado = reservas.some(r => {
            const rStart = timeToSlot(r.hora_inicio);
            const rEnd = timeToSlot(r.hora_fin);
            return r.sala_id === sala.id && slotIdx >= rStart && slotIdx < rEnd;
          });
          if (!ocupado) {
            proximoDisponible = { sala: sala.nombre, hora: `${slotHour}:${slotMin.toString().padStart(2, '0')}` };
            break;
          }
        }
        if (proximoDisponible) break;
      }
    }

    return {
      totalReservas: reservas.length,
      horasOcupadas: horasOcupadas.toFixed(1),
      porcentajeOcupacion: porcentajeOcupacion.toFixed(0),
      salaMasUsada,
      proximoDisponible
    };
  }, [reservas, salas, totalSlots, slotMinutes, timeToSlot]);

  const formatHour12 = (hora) => {
    const [h, m] = hora.split(':').map(Number);
    const period = h >= 12 ? 'PM' : 'AM';
    const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${hour12}:${m.toString().padStart(2, '0')} ${period}`;
  };

  return (
    <div className="space-y-4">
      {/* Ocupación del día */}
      <div>
        <div className="flex items-center justify-between text-sm mb-1">
          <span className="text-gray-600">Ocupación del día</span>
          <span className="font-semibold text-gray-900">{stats.porcentajeOcupacion}%</span>
        </div>
        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-blue-500 rounded-full transition-all"
            style={{ width: `${stats.porcentajeOcupacion}%` }}
          ></div>
        </div>
      </div>

      {/* Stats en grid */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-gray-50 rounded-lg p-3">
          <p className="text-2xl font-bold text-gray-900">{stats.totalReservas}</p>
          <p className="text-xs text-gray-500">Reservas hoy</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-3">
          <p className="text-2xl font-bold text-gray-900">{stats.horasOcupadas}h</p>
          <p className="text-xs text-gray-500">Horas ocupadas</p>
        </div>
      </div>

      {/* Sala más usada */}
      {stats.salaMasUsada && (
        <div className="bg-blue-50 rounded-lg p-3">
          <p className="text-xs text-blue-600 mb-1">Sala más solicitada</p>
          <p className="font-medium text-blue-900">{stats.salaMasUsada.nombre}</p>
        </div>
      )}

      {/* Próximo disponible */}
      {stats.proximoDisponible && (
        <div className="bg-green-50 rounded-lg p-3">
          <p className="text-xs text-green-600 mb-1">Próximo slot disponible</p>
          <p className="font-medium text-green-900">
            {stats.proximoDisponible.sala} a las {formatHour12(stats.proximoDisponible.hora)}
          </p>
        </div>
      )}

      {!stats.proximoDisponible && stats.totalReservas > 0 && (
        <div className="bg-yellow-50 rounded-lg p-3">
          <p className="text-xs text-yellow-600 mb-1">Estado</p>
          <p className="font-medium text-yellow-900">Sin disponibilidad hoy</p>
        </div>
      )}
    </div>
  );
};

const RoomReservationView = () => {
  const { user } = useAuth();
  const [selectedSede, setSelectedSede] = useState('CA1');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [salas, setSalas] = useState([]);
  const [reservas, setReservas] = useState([]);
  const [reservasSede, setReservasSede] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedReserva, setSelectedReserva] = useState(null);
  const [newReserva, setNewReserva] = useState({
    sala_id: null,
    hora_inicio: '',
    hora_fin: '',
    motivo: '',
    solicitante: null
  });

  // Lista de empleados para el buscador
  const [empleados, setEmpleados] = useState([]);

  // Estado para drag-and-drop (crear nuevo)
  const [isDragging, setIsDragging] = useState(false);
  const [dragSala, setDragSala] = useState(null);
  const [dragStartSlot, setDragStartSlot] = useState(null);
  const [dragEndSlot, setDragEndSlot] = useState(null);

  // Estado para mover reserva existente
  const [isMoving, setIsMoving] = useState(false);
  const [movingReserva, setMovingReserva] = useState(null);
  const [moveTargetSala, setMoveTargetSala] = useState(null);
  const [moveTargetSlot, setMoveTargetSlot] = useState(null);
  const [moveOffset, setMoveOffset] = useState(0);
  const [isOutsideTimeline, setIsOutsideTimeline] = useState(false);

  // Estado para resize de reserva
  const [isResizing, setIsResizing] = useState(false);
  const [resizingReserva, setResizingReserva] = useState(null);
  const [resizeEdge, setResizeEdge] = useState(null); // 'start' o 'end'
  const [resizeTargetSlot, setResizeTargetSlot] = useState(null);

  // Hora actual para la línea roja
  const [currentTime, setCurrentTime] = useState(new Date());

  const gridRef = useRef(null);
  // Scroll horizontal de la grilla: la etiqueta de la hora (fuera del panel) lo sigue.
  const [scrollGrid, setScrollGrid] = useState(0);

  // Configuración de tiempo - slots de 30 minutos
  const START_HOUR = 8;
  const END_HOUR = 17; // 5 PM
  const SLOT_MINUTES = 30;
  const SLOTS_PER_HOUR = 60 / SLOT_MINUTES;
  const TOTAL_SLOTS = (END_HOUR - START_HOUR) * SLOTS_PER_HOUR;
  const SLOT_WIDTH = 75; // Ancho de cada slot de 30 min
  const ROW_HEIGHT = 80;

  // Generar array de slots (cada slot = 30 min)
  const slots = Array.from({ length: TOTAL_SLOTS }, (_, i) => {
    const totalMinutes = START_HOUR * 60 + i * SLOT_MINUTES;
    const hour = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return { hour, minutes, index: i };
  });

  // Convertir slot index a tiempo string "HH:MM"
  const slotToTime = (slotIndex) => {
    const totalMinutes = START_HOUR * 60 + slotIndex * SLOT_MINUTES;
    const hour = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return `${hour.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  // Convertir tiempo string "HH:MM" a slot index
  const timeToSlot = (timeStr) => {
    const [hour, minutes] = timeStr.split(':').map(Number);
    const totalMinutes = hour * 60 + minutes;
    return (totalMinutes - START_HOUR * 60) / SLOT_MINUTES;
  };

  // Formatear fecha en formato YYYY-MM-DD (hora local, NO UTC)
  const formatDateLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Formatear hora en formato 12h
  const formatTime = (timeStr) => {
    const [hour, minutes] = timeStr.split(':').map(Number);
    const period = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${hour12}:${minutes.toString().padStart(2, '0')} ${period}`;
  };

  const formatHourShort = (hour) => {
    if (hour === 12) return '12 PM';
    if (hour === 0) return '12 AM';
    if (hour < 12) return `${hour} AM`;
    return `${hour - 12} PM`;
  };

  useEffect(() => {
    fetchSalas();
  }, [user?.role]);

  useEffect(() => {
    fetchEmpleados();
  }, []);

  // Actualizar hora actual cada segundo
  useEffect(() => {
    const updateTime = () => setCurrentTime(new Date());
    const interval = setInterval(updateTime, 1000); // cada segundo para el reloj
    return () => clearInterval(interval);
  }, []);

  // Verificar si la fecha seleccionada es hoy
  const isToday = useMemo(() => {
    const today = new Date();
    return selectedDate.toDateString() === today.toDateString();
  }, [selectedDate]);

  // Calcular posición de la línea de hora actual. Va con segundos y se desliza
  // entre un tic y otro: la línea corre continua en vez de saltar cada minuto.
  const currentTimePosition = useMemo(() => {
    if (!isToday) return null;
    const hours = currentTime.getHours();
    const minutes = currentTime.getMinutes() + currentTime.getSeconds() / 60;
    if (hours < START_HOUR || hours >= END_HOUR) return null;
    const totalMinutes = (hours - START_HOUR) * 60 + minutes;
    return (totalMinutes / SLOT_MINUTES) * SLOT_WIDTH;
  }, [currentTime, isToday]);

  // Lo que ya paso del dia se sombrea hasta la linea de ahora (todo el dia si
  // es un dia anterior, nada si es uno futuro).
  const anchoPasado = useMemo(() => {
    const total = TOTAL_SLOTS * SLOT_WIDTH;
    if (isToday) {
      if (currentTimePosition !== null) return currentTimePosition;
      return currentTime.getHours() >= END_HOUR ? total : 0;
    }
    const inicioDia = new Date(selectedDate);
    inicioDia.setHours(0, 0, 0, 0);
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    return inicioDia < hoy ? total : 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isToday, currentTimePosition, selectedDate, currentTime]);

  const fetchEmpleados = async () => {
    try {
      const response = await axiosInstance.get('/api/empleados');
      setEmpleados(response.data.map(emp => ({
        id: emp.id,
        nombre: emp.nombre,
        ficha: emp.ficha
      })));
    } catch (error) {
      console.error('Error fetching empleados:', error);
    }
  };

  useEffect(() => {
    if (salas.length > 0) {
      fetchReservas();
    }
  }, [selectedDate, selectedSede, salas]);

  const fetchSalas = async () => {
    try {
      const response = await axiosInstance.get('/api/salas/');
      const salasData = response.data || [];
      const missingCcs = !salasData.some(sala => sala.sede === 'CCS');
      const canSeedSalas = user?.role === 'admin' || user?.role === 'ti';

      if ((salasData.length === 0 || missingCcs) && canSeedSalas) {
        await axiosInstance.post('/api/salas/seed');
        const retry = await axiosInstance.get('/api/salas/');
        setSalas(retry.data);
        return;
      }

      setSalas(salasData);
    } catch (error) {
      console.error('Error fetching salas:', error);
    }
  };

  // El spinner sale solo en la primera carga. Despues, al cambiar de dia o de
  // sede, la grilla se queda y las reservas se recogen y se dibujan en su lugar
  // (si la grilla se desmontara, el barrido se repetiria y parpadearia). Una
  // respuesta vieja que llega tarde no pisa a la del dia elegido.
  const consultaReservas = useRef(0);
  const fetchReservas = async () => {
    const consulta = ++consultaReservas.current;
    try {
      const dateStr = formatDateLocal(selectedDate);
      const response = await axiosInstance.get(`/api/salas/reservas?fecha=${dateStr}&sede=${selectedSede}`);
      if (consulta === consultaReservas.current) setReservas(response.data);
    } catch (error) {
      console.error('Error fetching reservas:', error);
    } finally {
      if (consulta === consultaReservas.current) setLoading(false);
    }
  };

  const fetchReservasSede = async () => {
    try {
      const response = await axiosInstance.get(`/api/salas/reservas?sede=${selectedSede}`);
      setReservasSede(response.data || []);
    } catch (error) {
      console.error('Error fetching reservas de sede:', error);
      setReservasSede([]);
    }
  };

  useEffect(() => {
    if (salas.length > 0) {
      fetchReservasSede();
    }
  }, [selectedSede, salas]);

  const handlePrevDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() - 1);
    setSelectedDate(newDate);
  };

  const handleNextDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + 1);
    setSelectedDate(newDate);
  };

  const handleToday = () => {
    setSelectedDate(new Date());
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('es-VE', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const isRecepcion = user?.role === 'recepcion';

  // Verificar si un slot está en el pasado
  // Un slot es pasado solo si ya terminó completamente (hora fin < ahora)
  const isPastSlot = (slotIndex) => {
    const now = new Date();
    const slotEndDate = new Date(selectedDate);
    // Calcular la hora de FIN del slot (inicio + 30 min)
    const endMinutes = START_HOUR * 60 + (slotIndex + 1) * SLOT_MINUTES;
    slotEndDate.setHours(Math.floor(endMinutes / 60), endMinutes % 60, 0, 0);
    return slotEndDate <= now;
  };

  // Obtener reservas para una sala específica
  const getReservasForSala = (salaId) => {
    return reservas.filter(r => r.sala_id === salaId);
  };

  // Verificar si una sala está en uso ahora mismo
  const isSalaEnUso = useCallback((salaId) => {
    if (!isToday) return null; // null = no aplica (no es hoy)
    const now = currentTime;
    const nowStr = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}`;
    return reservas.some(r =>
      r.sala_id === salaId &&
      r.estado === 'aprobada' &&
      r.hora_inicio <= nowStr &&
      r.hora_fin > nowStr
    );
  }, [reservas, currentTime, isToday]);

  // Verificar si hay conflicto con reservas existentes (excluyendo una reserva específica)
  const hasConflict = (salaId, startSlot, endSlot, excludeReservaId = null) => {
    const salaReservas = getReservasForSala(salaId);
    return salaReservas.some(r => {
      if (excludeReservaId && r.id === excludeReservaId) return false;
      const rStart = timeToSlot(r.hora_inicio);
      const rEnd = timeToSlot(r.hora_fin);
      return startSlot < rEnd && endSlot > rStart;
    });
  };

  // Calcular slot desde posición X del mouse
  const getSlotFromX = useCallback((clientX) => {
    if (!gridRef.current) return 0;
    const gridRect = gridRef.current.getBoundingClientRect();
    const scrollLeft = gridRef.current.scrollLeft;
    const relativeX = clientX - gridRect.left + scrollLeft - 130;
    const slotIndex = Math.floor(relativeX / SLOT_WIDTH);
    return Math.min(Math.max(slotIndex, 0), TOTAL_SLOTS);
  }, []);

  // Calcular sala desde posición Y del mouse
  const getSalaFromY = useCallback((clientY) => {
    if (!gridRef.current) return null;
    const gridRect = gridRef.current.getBoundingClientRect();
    const scrollTop = gridRef.current.scrollTop;
    const relativeY = clientY - gridRect.top + scrollTop - 45; // 45 = altura header
    const salaIndex = Math.floor(relativeY / ROW_HEIGHT);
    const filteredSalas = salas.filter(s => s.sede === selectedSede).sort((a, b) => a.id - b.id);
    if (salaIndex >= 0 && salaIndex < filteredSalas.length) {
      return filteredSalas[salaIndex];
    }
    return null;
  }, [salas, selectedSede]);

  // ============ DRAG PARA CREAR NUEVA RESERVA ============
  const handleMouseDownCreate = useCallback((e, sala, slotIndex) => {
    if (!isRecepcion || isPastSlot(slotIndex)) return;
    if (hasConflict(sala.id, slotIndex, slotIndex + 1)) return;

    e.preventDefault();
    setIsDragging(true);
    setDragSala(sala);
    setDragStartSlot(slotIndex);
    setDragEndSlot(slotIndex + 1);
  }, [isRecepcion, selectedDate, reservas]);

  // ============ DRAG PARA MOVER RESERVA EXISTENTE ============
  const handleMouseDownMove = useCallback((e, reserva) => {
    if (!isRecepcion) return;
    e.preventDefault();
    e.stopPropagation();

    const startSlot = timeToSlot(reserva.hora_inicio);
    const clickedSlot = getSlotFromX(e.clientX);
    const offset = clickedSlot - startSlot;

    setIsMoving(true);
    setMovingReserva(reserva);
    setMoveOffset(offset);
    setMoveTargetSala(salas.find(s => s.id === reserva.sala_id));
    setMoveTargetSlot(startSlot);
  }, [isRecepcion, salas, getSlotFromX]);

  // ============ RESIZE DE RESERVA EXISTENTE ============
  const handleMouseDownResize = useCallback((e, reserva, edge) => {
    if (!isRecepcion) return;
    e.preventDefault();
    e.stopPropagation();

    const startSlot = timeToSlot(reserva.hora_inicio);
    const endSlot = timeToSlot(reserva.hora_fin);

    setIsResizing(true);
    setResizingReserva(reserva);
    setResizeEdge(edge);
    setResizeTargetSlot(edge === 'start' ? startSlot : endSlot);
  }, [isRecepcion]);

  // ============ MOUSE MOVE GLOBAL ============
  const handleMouseMove = useCallback((e) => {
    // Drag para crear
    if (isDragging && gridRef.current && dragSala) {
      const slotIndex = getSlotFromX(e.clientX);

      if (slotIndex !== dragEndSlot) {
        const newStart = Math.min(dragStartSlot, slotIndex);
        const newEnd = Math.max(dragStartSlot, slotIndex);
        const actualEnd = newEnd === newStart ? newEnd + 1 : newEnd;

        if (!hasConflict(dragSala.id, newStart, actualEnd)) {
          setDragEndSlot(slotIndex);
        }
      }
    }

    // Drag para mover
    if (isMoving && gridRef.current && movingReserva) {
      const slotIndex = getSlotFromX(e.clientX);
      const targetSala = getSalaFromY(e.clientY);

      // Detectar si está fuera del timeline (arriba o abajo de las salas)
      if (!targetSala) {
        setIsOutsideTimeline(true);
        setMoveTargetSala(null);
      } else {
        setIsOutsideTimeline(false);
        const duration = timeToSlot(movingReserva.hora_fin) - timeToSlot(movingReserva.hora_inicio);
        const newStartSlot = Math.max(0, slotIndex - moveOffset);
        const newEndSlot = Math.min(TOTAL_SLOTS, newStartSlot + duration);

        // Verificar límites y conflictos
        if (newEndSlot <= TOTAL_SLOTS && !isPastSlot(newStartSlot)) {
          if (!hasConflict(targetSala.id, newStartSlot, newEndSlot, movingReserva.id)) {
            setMoveTargetSala(targetSala);
            setMoveTargetSlot(newStartSlot);
          }
        }
      }
    }

    // Resize de reserva
    if (isResizing && gridRef.current && resizingReserva) {
      const slotIndex = getSlotFromX(e.clientX);
      const startSlot = timeToSlot(resizingReserva.hora_inicio);
      const endSlot = timeToSlot(resizingReserva.hora_fin);

      if (resizeEdge === 'start') {
        // Mover inicio (mínimo 1 slot antes del fin, y no en el pasado)
        const newStart = Math.min(slotIndex, endSlot - 1);
        if (newStart >= 0 && !isPastSlot(newStart) && !hasConflict(resizingReserva.sala_id, newStart, startSlot, resizingReserva.id)) {
          setResizeTargetSlot(newStart);
        }
      } else {
        // Mover fin (mínimo 1 slot después del inicio)
        const newEnd = Math.max(slotIndex, startSlot + 1);
        if (newEnd <= TOTAL_SLOTS && !hasConflict(resizingReserva.sala_id, endSlot, newEnd, resizingReserva.id)) {
          setResizeTargetSlot(newEnd);
        }
      }
    }
  }, [isDragging, isMoving, isResizing, dragSala, dragStartSlot, dragEndSlot, movingReserva, moveOffset, resizingReserva, resizeEdge, getSlotFromX, getSalaFromY, reservas]);

  // ============ MOUSE UP GLOBAL ============
  const handleMouseUp = useCallback(async () => {
    // Finalizar drag para crear
    if (isDragging && dragSala) {
      const startSlot = Math.min(dragStartSlot, dragEndSlot);
      let endSlot = Math.max(dragStartSlot, dragEndSlot);
      if (startSlot === endSlot) endSlot = startSlot + 1;

      setNewReserva({
        sala_id: dragSala.id,
        hora_inicio: slotToTime(startSlot),
        hora_fin: slotToTime(endSlot),
        motivo: '',
        solicitante: null
      });
      setShowModal(true);

      setIsDragging(false);
      setDragSala(null);
      setDragStartSlot(null);
      setDragEndSlot(null);
    }

    // Finalizar drag para mover
    if (isMoving && movingReserva) {
      // Si está fuera del timeline, abrir modal de edición
      if (isOutsideTimeline) {
        setSelectedReserva(movingReserva);
        setShowDetailModal(true);
      } else if (moveTargetSala) {
        // Mover la reserva
        const duration = timeToSlot(movingReserva.hora_fin) - timeToSlot(movingReserva.hora_inicio);
        const newStartTime = slotToTime(moveTargetSlot);
        const newEndTime = slotToTime(moveTargetSlot + duration);

        // Solo actualizar si cambió algo
        const originalStartSlot = timeToSlot(movingReserva.hora_inicio);
        if (moveTargetSlot !== originalStartSlot || moveTargetSala.id !== movingReserva.sala_id) {
          try {
            await axiosInstance.put(`/api/salas/reservas/${movingReserva.id}`, {
              sala_id: moveTargetSala.id,
              hora_inicio: newStartTime,
              hora_fin: newEndTime
            });
            fetchReservas();
          } catch (error) {
            alert(error.response?.data?.error || 'Error al mover la reserva');
          }
        }
      }

      setIsMoving(false);
      setMovingReserva(null);
      setMoveTargetSala(null);
      setMoveTargetSlot(null);
      setMoveOffset(0);
      setIsOutsideTimeline(false);
    }

    // Finalizar resize
    if (isResizing && resizingReserva && resizeTargetSlot !== null) {
      const startSlot = timeToSlot(resizingReserva.hora_inicio);
      const endSlot = timeToSlot(resizingReserva.hora_fin);

      let newStartTime, newEndTime;
      if (resizeEdge === 'start') {
        newStartTime = slotToTime(resizeTargetSlot);
        newEndTime = resizingReserva.hora_fin;
      } else {
        newStartTime = resizingReserva.hora_inicio;
        newEndTime = slotToTime(resizeTargetSlot);
      }

      // Solo actualizar si cambió algo
      const changed = resizeEdge === 'start'
        ? resizeTargetSlot !== startSlot
        : resizeTargetSlot !== endSlot;

      if (changed) {
        try {
          await axiosInstance.put(`/api/salas/reservas/${resizingReserva.id}`, {
            sala_id: resizingReserva.sala_id,
            hora_inicio: newStartTime,
            hora_fin: newEndTime
          });
          fetchReservas();
        } catch (error) {
          alert(error.response?.data?.error || 'Error al redimensionar la reserva');
        }
      }

      setIsResizing(false);
      setResizingReserva(null);
      setResizeEdge(null);
      setResizeTargetSlot(null);
    }
  }, [isDragging, isMoving, isResizing, isOutsideTimeline, dragSala, dragStartSlot, dragEndSlot, movingReserva, moveTargetSala, moveTargetSlot, resizingReserva, resizeEdge, resizeTargetSlot]);

  // Agregar listeners globales
  useEffect(() => {
    if (isDragging || isMoving || isResizing) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, isMoving, isResizing, handleMouseMove, handleMouseUp]);

  const handleReservaClick = (e, reserva) => {
    e.stopPropagation();
    if (!isMoving) {
      setSelectedReserva(reserva);
      setShowDetailModal(true);
    }
  };

  const handleCreateReserva = async () => {
    try {
      const dateStr = formatDateLocal(selectedDate);
      await axiosInstance.post('/api/salas/reservas', {
        sala_id: newReserva.sala_id,
        hora_inicio: newReserva.hora_inicio,
        hora_fin: newReserva.hora_fin,
        motivo: newReserva.motivo,
        solicitante_id: newReserva.solicitante?.id || null,
        fecha: dateStr
      });
      setShowModal(false);
      setNewReserva({ sala_id: null, hora_inicio: '', hora_fin: '', motivo: '', solicitante: null });
      fetchReservas();
    } catch (error) {
      alert(error.response?.data?.error || 'Error al crear la reserva');
    }
  };

  const handleEliminar = async (id) => {
    if (!window.confirm('¿Eliminar esta reserva?')) return;
    try {
      await axiosInstance.delete(`/api/salas/reservas/${id}`);
      setShowDetailModal(false);
      fetchReservas();
    } catch (error) {
      alert(error.response?.data?.error || 'Error al eliminar');
    }
  };

  const salasFilteredBySede = salas.filter(s => s.sede === selectedSede).sort((a, b) => a.id - b.id);

  // Generar URL de Google Calendar para una reserva
  const generateGoogleCalendarUrl = (reserva) => {
    const fecha = reserva.fecha; // YYYY-MM-DD
    const horaInicio = reserva.hora_inicio; // HH:MM
    const horaFin = reserva.hora_fin; // HH:MM

    // Formato: YYYYMMDDTHHmmss (sin timezone, Google Calendar usará la del usuario)
    const startDt = `${fecha.replace(/-/g, '')}T${horaInicio.replace(':', '')}00`;
    const endDt = `${fecha.replace(/-/g, '')}T${horaFin.replace(':', '')}00`;

    const title = encodeURIComponent(
      reserva.motivo
        ? `${reserva.motivo} - ${reserva.sala?.nombre || 'Sala'}`
        : `Reunión - ${reserva.sala?.nombre || 'Sala'}`
    );
    const location = encodeURIComponent(
      `${reserva.sala?.nombre || 'Sala'}, Sede ${reserva.sala?.sede || ''}, SURA de Venezuela`
    );
    const details = encodeURIComponent(
      `Reserva de sala en Centro SURA\n` +
      `Sala: ${reserva.sala?.nombre || ''} (Sede ${reserva.sala?.sede || ''})\n` +
      `Solicitante: ${reserva.solicitante?.nombre || 'N/A'}\n` +
      (reserva.motivo ? `Motivo: ${reserva.motivo}` : '')
    );

    return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${startDt}/${endDt}&details=${details}&location=${location}&ctz=America/Caracas`;
  };

  // Calcular posición y ancho de una reserva
  const getReservaStyle = (reserva) => {
    const startSlot = timeToSlot(reserva.hora_inicio);
    const endSlot = timeToSlot(reserva.hora_fin);
    const left = startSlot * SLOT_WIDTH;
    const width = (endSlot - startSlot) * SLOT_WIDTH - 4;
    return { left: `${left}px`, width: `${width}px` };
  };

  // Calcular preview del drag para crear
  const getDragPreviewStyle = () => {
    if (!isDragging || dragStartSlot === null || dragEndSlot === null) return null;

    const startSlot = Math.min(dragStartSlot, dragEndSlot);
    let endSlot = Math.max(dragStartSlot, dragEndSlot);
    if (startSlot === endSlot) endSlot = startSlot + 1;

    const left = startSlot * SLOT_WIDTH;
    const width = (endSlot - startSlot) * SLOT_WIDTH - 4;
    return { left: `${left}px`, width: `${width}px` };
  };

  // Calcular preview para mover
  const getMovePreviewStyle = () => {
    if (!isMoving || !movingReserva || moveTargetSlot === null) return null;

    const duration = timeToSlot(movingReserva.hora_fin) - timeToSlot(movingReserva.hora_inicio);
    const left = moveTargetSlot * SLOT_WIDTH;
    const width = duration * SLOT_WIDTH - 4;
    return { left: `${left}px`, width: `${width}px` };
  };

  // Generar opciones de tiempo para los selects
  const timeOptions = Array.from({ length: TOTAL_SLOTS + 1 }, (_, i) => {
    const time = slotToTime(i);
    return { value: time, label: formatTime(time) };
  });

  return (
    <MotionConfig reducedMotion="user">
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-4 flex-wrap gap-4">
        <h2 className="page-title">Reserva de Salas</h2>
        <span className="text-sm text-gray-400 font-normal italic">
          {isRecepcion ? 'Arrastra sobre una franja disponible para crear reservas' : 'Para reservar una sala, llamar al ext. 1900 — Recepción'}
        </span>
      </div>

      {/* Controls */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          {/* Sede Tabs */}
          <div className="flex bg-gray-100 rounded-lg p-1">
            {['CA1', 'CDN', 'CCS'].map(sede => (
              <button
                key={sede}
                onClick={() => setSelectedSede(sede)}
                className={`relative px-4 py-2 rounded-md font-medium transition-colors ${selectedSede === sede
                  ? 'text-white'
                  : 'text-gray-600 hover:text-gray-900'
                  }`}
              >
                {selectedSede === sede && (
                  <motion.span
                    layoutId="reservas-sede"
                    transition={{ type: 'spring', stiffness: 520, damping: 40 }}
                    className="absolute inset-0 rounded-md bg-blue-600 shadow"
                  />
                )}
                <span className="relative">
                  <MapPin size={16} className="inline mr-1" />
                  {sede}
                </span>
              </button>
            ))}
          </div>

          {/* Date Navigation */}
          <div className="flex items-center gap-2">
            <button
              onClick={handlePrevDay}
              className="p-2 hover:bg-gray-100 rounded-lg transition"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              onClick={handleToday}
              className="px-3 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-lg transition"
            >
              Hoy
            </button>
            <div className="px-4 py-2 font-medium text-gray-700 min-w-[280px] text-center">
              {formatDate(selectedDate)}
            </div>
            <button
              onClick={handleNextDay}
              className="p-2 hover:bg-gray-100 rounded-lg transition"
            >
              <ChevronRight size={20} />
            </button>
          </div>

          {/* Reloj de hora actual */}
          <div className="flex items-center gap-2 bg-gray-100 px-4 py-2 rounded-lg">
            <Clock size={18} className="text-blue-600" />
            <span className="font-mono text-lg font-semibold text-gray-800">
              {currentTime.toLocaleTimeString('es-VE', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
            </span>
          </div>

          {/* Info */}
          <div className="flex items-center gap-3 text-sm text-gray-500">
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded bg-blue-500"></div>
              <span>Reservado</span>
            </div>
            {isRecepcion && (
              <div className="flex items-center gap-1">
                <GripVertical size={14} />
                <span>Arrastra para mover</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Hora actual: arriba del panel, sobre la linea roja (asi no tapa las
          horas). Corre con la linea y sigue el scroll horizontal de la grilla.
          131 = borde del panel + columna de salas. */}
      <div className="relative h-7 -mt-4 overflow-hidden pointer-events-none" aria-hidden="true">
        {currentTimePosition !== null && currentTimePosition - scrollGrid >= 0 && (
          <div className="absolute inset-y-0" style={{ left: 131 - scrollGrid }}>
            <div
              className="absolute bottom-0 flex -translate-x-1/2 flex-col items-center"
              style={{ left: `${currentTimePosition + 1}px`, transition: AHORA_DESLIZA }}
            >
              <span className="rounded-md bg-red-500 px-1.5 py-0.5 text-[11px] font-semibold leading-tight tabular-nums text-white shadow-sm">
                {currentTime.getHours().toString().padStart(2, '0')}:{currentTime.getMinutes().toString().padStart(2, '0')}
              </span>
              <span className="h-1.5 w-0.5 bg-red-500" />
            </div>
          </div>
        )}
      </div>

      {/* Grid Calendar */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-500">
            <div className="w-12 h-12 rounded-full border-3 border-gray-200 border-t-blue-600 animate-spin" />
            <p className="mt-4 text-sm font-medium text-shimmer">Cargando reservas...</p>
          </div>
        ) : (
          <div
            ref={gridRef}
            onScroll={(e) => setScrollGrid(e.currentTarget.scrollLeft)}
            className="overflow-x-auto select-none"
            style={{ cursor: isDragging ? 'col-resize' : isMoving ? 'grabbing' : 'default' }}
          >
            <div className="min-w-max">
              {/* Header con horas */}
              <div className="flex border-b border-gray-200 bg-gray-50 sticky top-0 z-10">
                <div className="w-[130px] flex-shrink-0 p-3 font-medium text-gray-700 border-r border-gray-200">
                  Sala
                </div>
                <div className="flex relative">
                  {Array.from({ length: END_HOUR - START_HOUR }, (_, i) => i + START_HOUR).map(hour => (
                    <div
                      key={hour}
                      className="border-r border-gray-200 p-2 text-center font-medium text-gray-600"
                      style={{ width: `${SLOT_WIDTH * SLOTS_PER_HOUR}px` }}
                    >
                      {formatHourShort(hour)}
                    </div>
                  ))}
                  {/* Indicador de hora actual en el header */}
                  {currentTimePosition !== null && (
                    <div
                      className="absolute top-0 bottom-0 w-0.5 bg-red-500 z-30 pointer-events-none"
                      style={{ left: `${currentTimePosition}px`, transition: AHORA_DESLIZA }}
                    >
                    </div>
                  )}
                </div>
              </div>

              {/* Filas de salas */}
              {salasFilteredBySede.map((sala, salaIndex) => (
                <div key={sala.id} className="flex border-b border-gray-200 hover:bg-gray-50/50">
                  {/* Nombre de sala */}
                  <div className="w-[130px] flex-shrink-0 p-3 font-medium text-gray-800 border-r border-gray-200 bg-white">
                    <div className="flex items-center gap-2">
                      <Users size={16} className="text-gray-400" />
                      <span>{sala.nombre}</span>
                      {isToday && (() => {
                        const enUso = isSalaEnUso(sala.id);
                        return enUso ? (
                          <span className="relative flex h-2.5 w-2.5" title="En uso">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500"></span>
                          </span>
                        ) : (
                          <span className="relative flex h-2.5 w-2.5" title="Disponible">
                            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
                          </span>
                        );
                      })()}
                    </div>
                  </div>

                  {/* Timeline de la sala */}
                  <div
                    className="relative flex-1"
                    style={{ height: `${ROW_HEIGHT}px` }}
                  >
                    {/* Grid de slots (fondo) */}
                    <div className="absolute inset-0 flex">
                      {slots.map((slot, idx) => {
                        const isPast = isPastSlot(idx);
                        const isHourStart = slot.minutes === 0;
                        const canInteract = isRecepcion && !isPast && !hasConflict(sala.id, idx, idx + 1);

                        return (
                          <div
                            key={idx}
                            className={`${isHourStart ? 'border-r border-gray-200' : 'border-r border-gray-100'} ${!isPast && canInteract ? 'hover:bg-blue-50' : ''
                              }`}
                            style={{ width: `${SLOT_WIDTH}px`, cursor: canInteract ? 'crosshair' : 'default' }}
                            onMouseDown={(e) => canInteract && handleMouseDownCreate(e, sala, idx)}
                          />
                        );
                      })}
                    </div>

                    {/* Lo que ya paso del dia: sombra que avanza con la linea de ahora */}
                    {anchoPasado > 0 && (
                      <div
                        className="absolute inset-y-0 left-0 bg-gray-100/80 pointer-events-none"
                        style={{ width: `${anchoPasado}px`, transition: AHORA_DESLIZA_ANCHO }}
                      />
                    )}

                    {/* Reservas existentes */}
                    {getReservasForSala(sala.id).map(reserva => {
                      const style = getReservaStyle(reserva);
                      const isBeingMoved = isMoving && movingReserva?.id === reserva.id;
                      const isBeingResized = isResizing && resizingReserva?.id === reserva.id;

                      return (
                        <div
                          key={reserva.id}
                          className={`absolute top-1 bottom-1 rounded-lg shadow-sm overflow-hidden transition-all group ${isBeingMoved && isOutsideTimeline
                            ? 'opacity-60 bg-orange-500 ring-2 ring-orange-400'
                            : isBeingMoved || isBeingResized
                              ? 'opacity-40 bg-gray-400'
                              : 'bg-blue-500 hover:bg-blue-600'
                            }`}
                          style={style}
                          onClick={(e) => handleReservaClick(e, reserva)}
                        >
                          {/* Handle izquierdo para resize */}
                          {isRecepcion && !isBeingMoved && !isBeingResized && (
                            <div
                              className="absolute left-0 top-0 bottom-0 w-2 cursor-ew-resize hover:bg-blue-700/50 opacity-0 group-hover:opacity-100 transition-opacity"
                              onMouseDown={(e) => handleMouseDownResize(e, reserva, 'start')}
                            />
                          )}

                          {/* Contenido - área para mover */}
                          <div
                            className={`px-3 py-2 text-white h-full flex flex-col justify-center ${isRecepcion ? 'cursor-grab active:cursor-grabbing' : ''}`}
                            onMouseDown={(e) => isRecepcion && !isBeingMoved && !isBeingResized && handleMouseDownMove(e, reserva)}
                          >
                            <div className="text-sm font-medium truncate">
                              {reserva.solicitante ? reserva.solicitante.nombre : 'Sin solicitante'}
                            </div>
                            <div className="text-xs opacity-80 truncate">
                              {reserva.motivo || `${formatTime(reserva.hora_inicio)} - ${formatTime(reserva.hora_fin)}`}
                            </div>
                          </div>

                          {/* Handle derecho para resize */}
                          {isRecepcion && !isBeingMoved && !isBeingResized && (
                            <div
                              className="absolute right-0 top-0 bottom-0 w-2 cursor-ew-resize hover:bg-blue-700/50 opacity-0 group-hover:opacity-100 transition-opacity"
                              onMouseDown={(e) => handleMouseDownResize(e, reserva, 'end')}
                            />
                          )}
                        </div>
                      );
                    })}

                    {/* Preview del resize */}
                    {isResizing && resizingReserva?.sala_id === sala.id && (
                      <div
                        className="absolute top-1 bottom-1 bg-orange-400/70 rounded-lg border-2 border-orange-500 border-dashed pointer-events-none"
                        style={{
                          left: `${(resizeEdge === 'start' ? resizeTargetSlot : timeToSlot(resizingReserva.hora_inicio)) * SLOT_WIDTH}px`,
                          width: `${((resizeEdge === 'end' ? resizeTargetSlot : timeToSlot(resizingReserva.hora_fin)) - (resizeEdge === 'start' ? resizeTargetSlot : timeToSlot(resizingReserva.hora_inicio))) * SLOT_WIDTH - 4}px`,
                          transition: PREVIA_DESLIZA,
                        }}
                      >
                        <div className="p-2 text-white text-sm font-medium">
                          {formatTime(slotToTime(resizeEdge === 'start' ? resizeTargetSlot : timeToSlot(resizingReserva.hora_inicio)))} - {formatTime(slotToTime(resizeEdge === 'end' ? resizeTargetSlot : timeToSlot(resizingReserva.hora_fin)))}
                        </div>
                      </div>
                    )}

                    {/* Preview del drag para crear */}
                    {isDragging && dragSala?.id === sala.id && (
                      <div
                        className="absolute top-1 bottom-1 bg-blue-400/70 rounded-lg border-2 border-blue-500 border-dashed"
                        style={{ ...getDragPreviewStyle(), transition: PREVIA_DESLIZA }}
                      >
                        <div className="p-2 text-white text-sm font-medium">
                          {formatTime(slotToTime(Math.min(dragStartSlot, dragEndSlot)))} - {formatTime(slotToTime(Math.max(dragStartSlot, dragEndSlot) === Math.min(dragStartSlot, dragEndSlot) ? Math.min(dragStartSlot, dragEndSlot) + 1 : Math.max(dragStartSlot, dragEndSlot)))}
                        </div>
                      </div>
                    )}

                    {/* Preview para mover reserva */}
                    {isMoving && moveTargetSala?.id === sala.id && movingReserva && (
                      <div
                        className="absolute top-1 bottom-1 bg-green-400/70 rounded-lg border-2 border-green-500 border-dashed"
                        style={{ ...getMovePreviewStyle(), transition: PREVIA_DESLIZA }}
                      >
                        <div className="p-2 text-white text-sm font-medium">
                          {formatTime(slotToTime(moveTargetSlot))} - {formatTime(slotToTime(moveTargetSlot + (timeToSlot(movingReserva.hora_fin) - timeToSlot(movingReserva.hora_inicio))))}
                        </div>
                      </div>
                    )}

                    {/* Línea de hora actual */}
                    {currentTimePosition !== null && (
                      <div
                        className="absolute top-0 bottom-0 w-0.5 bg-red-500 z-20 pointer-events-none"
                        style={{ left: `${currentTimePosition}px`, transition: AHORA_DESLIZA }}
                      />
                    )}
                  </div>
                </div>
              ))}

            </div>
          </div>
        )}
      </div>

      {/* Indicador flotante cuando arrastra fuera del timeline */}
      {isMoving && isOutsideTimeline && movingReserva && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 bg-orange-500 text-white px-4 py-2 rounded-lg shadow-lg z-50 flex items-center gap-2 animate-pulse">
          <span className="text-sm font-medium">Suelta para ver/editar la reserva</span>
        </div>
      )}

      {/* Panel inferior: Calendario + Lista + Estadísticas */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
        {/* Mini Calendario Mensual */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 animate-fadeIn" style={{ animationDelay: '0.1s' }}>
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Calendar size={18} className="text-blue-600" />
            Calendario
          </h3>
            <MiniCalendar
              selectedDate={selectedDate}
              onSelectDate={setSelectedDate}
              reservasDates={reservasSede.map(r => r.fecha)}
            />
        </div>

        {/* Lista de Reservas del Día */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 animate-fadeIn" style={{ animationDelay: '0.2s' }}>
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <List size={18} className="text-blue-600" />
            Reservas del día
            <span className="ml-auto bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded-full">
              {reservas.length}
            </span>
          </h3>
          <div className="space-y-2 max-h-[280px] overflow-y-auto">
            {reservas.length === 0 ? (
              <p className="text-gray-500 text-sm text-center py-4">
                No hay reservas para este día
              </p>
            ) : (
              reservas
                .sort((a, b) => a.hora_inicio.localeCompare(b.hora_inicio))
                .map(reserva => (
                  <div
                    key={reserva.id}
                    className="p-3 bg-gray-50 rounded-lg hover:bg-blue-50 cursor-pointer transition"
                    onClick={() => {
                      setSelectedReserva(reserva);
                      setShowDetailModal(true);
                    }}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate flex items-center gap-1.5">
                          <User size={14} className="text-blue-600 flex-shrink-0" />
                          {reserva.solicitante ? reserva.solicitante.nombre : 'Sin solicitante'}
                        </p>
                        <p className="text-sm text-gray-500">
                          {reserva.sala?.nombre} • {formatTime(reserva.hora_inicio)} - {formatTime(reserva.hora_fin)}
                        </p>
                        {reserva.motivo && (
                          <p className="text-xs text-gray-400 mt-0.5 truncate">
                            {reserva.motivo}
                          </p>
                        )}
                      </div>
                      <div className="ml-2 w-2 h-2 rounded-full bg-blue-500 mt-2"></div>
                    </div>
                  </div>
                ))
            )}
          </div>
        </div>

        {/* Estadísticas */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 animate-fadeIn" style={{ animationDelay: '0.3s' }}>
          <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <BarChart3 size={18} className="text-blue-600" />
            Estadísticas
          </h3>
          <StatsPanel
            reservas={reservas}
            salas={salasFilteredBySede}
            totalSlots={TOTAL_SLOTS}
            slotMinutes={SLOT_MINUTES}
            timeToSlot={timeToSlot}
          />
        </div>
      </div>

      {/* Modal Crear Reserva */}
      {showModal && isRecepcion && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 animate-fadeIn">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-lg mx-4 animate-scaleIn">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">Nueva Reserva</h3>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>

            <div className="space-y-4">
              {/* Info de sala y fecha */}
              <div className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <p className="text-xs text-gray-500">Sala</p>
                  <p className="font-medium text-gray-900">
                    {salas.find(s => s.id === newReserva.sala_id)?.nombre} - {selectedSede}
                  </p>
                </div>
                <div className="flex-1">
                  <p className="text-xs text-gray-500">Fecha</p>
                  <p className="font-medium text-gray-900">
                    {selectedDate.toLocaleDateString('es-VE', { weekday: 'short', day: 'numeric', month: 'short' })}
                  </p>
                </div>
              </div>

              {/* Buscador de solicitante - PRINCIPAL */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Solicitante <span className="text-red-500">*</span>
                </label>
                <EmployeeSearch
                  value={newReserva.solicitante}
                  onChange={(emp) => setNewReserva({ ...newReserva, solicitante: emp })}
                  empleados={empleados}
                />
              </div>

              {/* Selector de horario */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Hora inicio</label>
                  <select
                    value={newReserva.hora_inicio}
                    onChange={(e) => setNewReserva({ ...newReserva, hora_inicio: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {timeOptions.slice(0, -1).map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Hora fin</label>
                  <select
                    value={newReserva.hora_fin}
                    onChange={(e) => setNewReserva({ ...newReserva, hora_fin: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {timeOptions.slice(1).map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Motivo - opcional */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descripción <span className="text-gray-400 font-normal">(opcional)</span>
                </label>
                <input
                  type="text"
                  value={newReserva.motivo}
                  onChange={(e) => setNewReserva({ ...newReserva, motivo: e.target.value })}
                  placeholder="Ej: Reunión de equipo, Capacitación..."
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowModal(false);
                  setNewReserva({ sala_id: null, hora_inicio: '', hora_fin: '', motivo: '', solicitante: null });
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition"
              >
                Cancelar
              </button>
              <button
                onClick={handleCreateReserva}
                disabled={!newReserva.solicitante}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Reservar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Detalle Reserva */}
      {showDetailModal && selectedReserva && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 animate-fadeIn">
          <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-md mx-4 animate-scaleIn">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900">Detalle de Reserva</h3>
              <button onClick={() => setShowDetailModal(false)} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>

            <div className="space-y-3">
              {/* Solicitante - Principal */}
              <div className="p-3 bg-blue-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <User size={18} className="text-blue-600" />
                  <div>
                    <p className="text-xs text-blue-600">Solicitante</p>
                    <p className="font-semibold text-gray-900">
                      {selectedReserva.solicitante ? selectedReserva.solicitante.nombre : 'Sin solicitante'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-gray-600">Sala</span>
                <span className="font-medium">{selectedReserva.sala?.nombre} - {selectedReserva.sala?.sede}</span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-gray-600">Fecha</span>
                <span className="font-medium">{selectedReserva.fecha}</span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-gray-600">Horario</span>
                <span className="font-medium">
                  {formatTime(selectedReserva.hora_inicio)} - {formatTime(selectedReserva.hora_fin)}
                </span>
              </div>

              <div className="flex items-center justify-between">
                <span className="text-gray-600">Creado por</span>
                <span className="font-medium text-gray-500 text-sm">{selectedReserva.usuario}</span>
              </div>

              {selectedReserva.motivo && (
                <div className="pt-2 border-t">
                  <span className="text-gray-600 text-sm">Descripción</span>
                  <p className="mt-1 text-gray-900">{selectedReserva.motivo}</p>
                </div>
              )}
            </div>

            {/* Botón Agendar en Google Calendar */}
            <a
              href={generateGoogleCalendarUrl(selectedReserva)}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-white border-2 border-blue-100 rounded-xl text-gray-700 hover:bg-blue-50 hover:border-blue-300 transition-all group"
            >
              <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
              </svg>
              <span className="font-medium text-sm">Agendar en Google Calendar</span>
              <ExternalLink size={14} className="text-gray-400 group-hover:text-blue-500 transition-colors" />
            </a>

            <div className="flex gap-3 mt-3">
              {isRecepcion && (
                <button
                  onClick={() => handleEliminar(selectedReserva.id)}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition flex items-center justify-center gap-2"
                >
                  <Trash2 size={18} />
                  Eliminar
                </button>
              )}

              <button
                onClick={() => setShowDetailModal(false)}
                className={`${isRecepcion ? '' : 'flex-1'} px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition`}
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </MotionConfig>
  );
};

export default RoomReservationView;
